import { createContext, createElement, useContext, useState, useEffect, useCallback, useRef, Fragment, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { api } from "./api";
import { switchAuthenticatedQueryScope, refreshAccessQueries } from "./queryClient";
import { savedAuthenticatedCacheScope } from "./auth-cache-scope";
import { OLD_TO_NEW_PERMISSION_MAP, NEW_TO_OLD_PERMISSION_MAP } from "../../../shared/permissions";
import { isProtectedAdministratorRole } from "../../../shared/roleProtection";

export type Permission = string;

// Permission from API
export interface PermissionDefinition {
  id: string;
  name: string;
  description: string | null;
  category: string;
}

// Role Definition from API
export interface RoleDefinition {
  id: string;
  name: string;
  description: string | null;
  workspaceId: string | null;
  isSystem: boolean;
  legacyRoleId?: string;
  permissions: PermissionDefinition[];
}
export interface RoleUpdateDraft {
  name?: string;
  description?: string;
  permissions?: string[];
  reason?: string;
  confirmed?: true;
  affectedPeopleCount?: number;
  expectedPermissions?: string[];
}

// Default Roles (fallback if API fails)
export const DEFAULT_ROLES: RoleDefinition[] = [
  { 
    id: "admin", 
    name: "Administrator", 
    description: "Full system access",
    workspaceId: null,
    isSystem: true,
    permissions: [
      { id: "view_brands", name: "View Brands", description: null, category: "inventory" },
      { id: "view_prices", name: "View Prices", description: null, category: "inventory" },
      { id: "manage_inventory", name: "Manage Inventory", description: null, category: "inventory" },
      { id: "create_items", name: "Create Items", description: null, category: "inventory" },
      { id: "edit_items", name: "Edit Items", description: null, category: "inventory" },
      { id: "delete_items", name: "Delete Items", description: null, category: "inventory" },
      { id: "manage_users", name: "Manage Users", description: null, category: "admin" },
      { id: "manage_roles", name: "Manage Roles", description: null, category: "admin" },
      { id: "manage_locations", name: "Manage Locations", description: null, category: "admin" },
      { id: "manage_categories", name: "Manage Categories", description: null, category: "inventory" },
      { id: "view_financials", name: "View Financials", description: null, category: "finance" },
      { id: "view_orders", name: "View Orders", description: null, category: "orders" },
      { id: "create_orders", name: "Create Orders", description: null, category: "orders" },
      { id: "approve_orders", name: "Approve Orders", description: null, category: "orders" },
      { id: "ship_orders", name: "Ship Orders", description: null, category: "orders" },
      { id: "receive_orders", name: "Receive Orders", description: null, category: "orders" },
      { id: "dispatch_orders", name: "Dispatch Orders", description: null, category: "orders" },
      { id: "manage_prices", name: "Manage Prices", description: null, category: "finance" },
      { id: "manage_delivery_slots", name: "Manage Delivery Slots", description: null, category: "orders" },
      { id: "supplier_receipts.edit_confirmed", name: "Directly Correct Confirmed Receipts", description: "Immediately edit a confirmed supplier receipt without approval; a reason is required and the change is recorded in the audit history.", category: "procurement" },
      { id: "supplier_receipts.review_change_requests", name: "Review Receipt Change Requests", description: "Approve or reject requested corrections to confirmed receipts and draft-receipt cancellation requests; requesters cannot approve their own requests.", category: "procurement" },
      { id: "branch_cash.view", name: "View Branch Cash", description: null, category: "branch_cash" },
      { id: "branch_cash.record", name: "Record Branch Cash", description: null, category: "branch_cash" },
      { id: "branch_cash.manage", name: "Manage Branch Cash", description: null, category: "branch_cash" },
      { id: "expense_setup.manage", name: "Manage Expense Setup", description: null, category: "branch_cash" },
    ]
  },
];

export interface User {
  id: string;
  name: string;
  email: string;
  roleId: string | null;
  locationIds: string[];
  managedLocationIds: string[];
  requestLocationIds: string[];
  deliveryLocationIds: string[];
  avatar?: string;
  workspaceIds: string[];
  canViewPrices?: boolean | null;
  defaultOrderFromLocationId?: string | null;
  defaultOrderToLocationId?: string | null;
}

function accessSignature(user: User | null, roles: RoleDefinition[]) {
  const role = roles.find(item => item.id === user?.roleId);
  const sorted = (ids: string[] | undefined) => [...(ids || [])].sort();
  return JSON.stringify([
    user?.id, user?.roleId, user?.canViewPrices,
    sorted(user?.workspaceIds), sorted(user?.locationIds), sorted(user?.managedLocationIds),
    sorted(user?.requestLocationIds), sorted(user?.deliveryLocationIds),
    role?.name, role?.isSystem, sorted(role?.permissions.map(permission => permission.id)),
  ]);
}

export interface Workspace {
  id: string;
  ownerUserId?: string | null;
  name: string;
  logo?: string;
  currency: string;
  timezone: string;
}

interface AuthContextType {
  user: User | null;
  users: User[];
  currentWorkspace: Workspace | null;
  availableWorkspaces: Workspace[];
  switchWorkspace: (workspaceId: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  demoLogin: (role: string) => Promise<void>;
  register: (data: { email: string; password: string; name: string; companyName: string }) => Promise<void>;
  logout: () => Promise<void>;
  switchRole: (userId: string) => void;
  addUser: (userData: Omit<User, 'id'> & { password: string }) => Promise<User | undefined>;
  updateUser: (user: User) => Promise<User | undefined>;
  deleteUser: (userId: string) => Promise<void>;
  roles: RoleDefinition[];
  addRole: (roleData: { name: string; description: string; workspaceId: string | null; isSystem: boolean; permissions: string[]; reason?: string; sourceRoleId?: string }) => Promise<RoleDefinition | undefined>;
  updateRole: (roleId: string, roleData: RoleUpdateDraft) => Promise<RoleDefinition | undefined>;
  deleteRole: (roleId: string, reason?: string) => Promise<void>;
  isLoading: boolean;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const authGeneration = useRef(0);
  const [accessVersion, setAccessVersion] = useState(0);
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [availableWorkspaces, setAvailableWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLocation] = useLocation();

  const loadUsers = async (workspaceId: string, generation = authGeneration.current) => {
    try {
      const usersData = await api.users.list(workspaceId);
      if (authGeneration.current === generation) setUsers(usersData);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      const generation = ++authGeneration.current;
      try {
        const { user: sessionUser, roles: sessionRoles } = await api.auth.getUser();
        if (generation !== authGeneration.current) return;
        const [workspaces, loadedRoles] = await Promise.all([
          api.workspaces.list(),
          sessionRoles && sessionRoles.length > 0
            ? Promise.resolve(sessionRoles)
            : api.roles.list().catch(error => { console.error("Failed to load roles:", error); return []; }),
        ]);
        if (generation !== authGeneration.current) return;
        const userWorkspaces = (workspaces as Workspace[]).filter((ws: Workspace) =>
          sessionUser.workspaceIds.includes(ws.id)
        );
        const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
        const defaultWorkspace = savedWorkspaceId
          ? userWorkspaces.find(ws => ws.id === savedWorkspaceId) || userWorkspaces[0]
          : userWorkspaces[0];
        await switchAuthenticatedQueryScope(queryClient, sessionUser.id, defaultWorkspace?.id ?? null, {
          preserveOfflineCache: savedAuthenticatedCacheScope() === JSON.stringify([sessionUser.id, defaultWorkspace?.id ?? null]),
        });
        if (generation !== authGeneration.current) return;
        setUser(sessionUser);
        setRoles(loadedRoles);
        setAvailableWorkspaces(userWorkspaces);
        if (defaultWorkspace) {
          setCurrentWorkspace(defaultWorkspace);
          localStorage.setItem('currentWorkspaceId', defaultWorkspace.id);
          void loadUsers(defaultWorkspace.id, generation);
        }
      } catch (error) {
        // Not authenticated - will stay on login
      } finally {
        if (generation === authGeneration.current) setIsLoading(false);
      }
    };

    checkSession();
    return () => { authGeneration.current += 1; };
  }, [queryClient]);

  const login = async (email: string, password: string) => {
    const generation = ++authGeneration.current;
    try {
      // Login now returns user, roles, and workspaces in one response
      const { user: loggedInUser, roles: loginRoles, workspaces: loginWorkspaces } = await api.auth.login(email, password);
      if (generation !== authGeneration.current) return;
      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      const defaultWorkspace = loginWorkspaces?.length
        ? savedWorkspaceId
          ? loginWorkspaces.find((ws: Workspace) => ws.id === savedWorkspaceId) || loginWorkspaces[0]
          : loginWorkspaces[0]
        : null;
      await switchAuthenticatedQueryScope(queryClient, loggedInUser.id, defaultWorkspace?.id ?? null);
      if (generation !== authGeneration.current) return;
      setUser(loggedInUser);
      setRoles(loginRoles || []);
      setAvailableWorkspaces(loginWorkspaces || []);
      setCurrentWorkspace(defaultWorkspace);
      setUsers([]);
      if (defaultWorkspace) {
        localStorage.setItem('currentWorkspaceId', defaultWorkspace.id);
        void loadUsers(defaultWorkspace.id, generation);
      }
      // Always redirect after successful login
      setLocation("/dashboard");
    } catch (error: any) {
      throw new Error(error.message || t("authPages.loginFailed"));
    }
  };

  const demoLogin = async (role: string) => {
    const generation = ++authGeneration.current;
    try {
      const { user: loggedInUser, roles: loginRoles, workspaces: loginWorkspaces } = await api.auth.demoLogin(role);
      if (generation !== authGeneration.current) return;
      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      const defaultWorkspace = loginWorkspaces?.length
        ? savedWorkspaceId
          ? loginWorkspaces.find((ws: Workspace) => ws.id === savedWorkspaceId) || loginWorkspaces[0]
          : loginWorkspaces[0]
        : null;
      await switchAuthenticatedQueryScope(queryClient, loggedInUser.id, defaultWorkspace?.id ?? null);
      if (generation !== authGeneration.current) return;
      setUser(loggedInUser);
      setRoles(loginRoles || []);
      setAvailableWorkspaces(loginWorkspaces || []);
      setCurrentWorkspace(defaultWorkspace);
      setUsers([]);
      if (defaultWorkspace) {
        localStorage.setItem('currentWorkspaceId', defaultWorkspace.id);
        void loadUsers(defaultWorkspace.id, generation);
      }
      setLocation("/dashboard");
    } catch (error: any) {
      throw new Error(error.message || t("authPages.demoLoginFailed"));
    }
  };

  const register = async (data: { email: string; password: string; name: string; companyName: string }) => {
    const generation = ++authGeneration.current;
    try {
      const { user: registeredUser, roles: regRoles, workspaces: regWorkspaces } = await api.auth.register(data);
      if (generation !== authGeneration.current) return;
      await switchAuthenticatedQueryScope(queryClient, registeredUser.id, regWorkspaces?.[0]?.id ?? null);
      if (generation !== authGeneration.current) return;
      setUser(registeredUser);
      setRoles(regRoles || []);
      setAvailableWorkspaces(regWorkspaces || []);
      setCurrentWorkspace(regWorkspaces?.[0] || null);
      setUsers([]);
      if (regWorkspaces?.length) {
        localStorage.setItem('currentWorkspaceId', regWorkspaces[0].id);
      }

      setLocation("/dashboard");
    } catch (error: any) {
      throw new Error(error.message || t("authPages.registrationFailedErr"));
    }
  };

  const logout = async () => {
    try {
      let clearCashPhotos: (() => Promise<number>) | undefined;
      // Only an explicit logout purges queued cash evidence. Transient auth
      // loading/network failures must not destroy photos saved on this phone.
      if (user?.id) {
        const { clearCashPhotosOnLogout, countCashPhotosOnDevice } = await import("@/components/branch-cash/cashPhotos");
        const pending = await countCashPhotosOnDevice(user.id);
        if (pending && !window.confirm(`${pending} cash receipt photo${pending === 1 ? " is" : "s are"} waiting on this device. Logging out deletes ${pending === 1 ? "it" : "them"}. Stay signed in to send ${pending === 1 ? "it" : "them"} when online?`)) return;
        if (pending) clearCashPhotos = () => clearCashPhotosOnLogout(user.id);
      }
      const generation = ++authGeneration.current;
      await api.auth.logout();
      if (generation !== authGeneration.current) return;
      if (clearCashPhotos) {
        try { await clearCashPhotos(); }
        catch (error) { console.error("Could not clear pending cash photos after logout:", error); }
      }
      await switchAuthenticatedQueryScope(queryClient, null, null);
      if (generation !== authGeneration.current) return;
      setUser(null);
      setUsers([]);
      setRoles([]);
      setCurrentWorkspace(null);
      setAvailableWorkspaces([]);
      localStorage.removeItem('currentWorkspaceId');
      setLocation("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const switchWorkspace = async (workspaceId: string) => {
    const workspace = availableWorkspaces.find(ws => ws.id === workspaceId);
    if (workspace && user?.workspaceIds.includes(workspaceId) && workspace.id !== currentWorkspace?.id) {
      const generation = ++authGeneration.current;
      // The server session must agree with the selected workspace before any
      // component can fetch data for it.
      await api.auth.setActiveWorkspace(workspaceId);
      const {user:workspaceUser,roles:workspaceRoles}=await api.auth.getUser();
      if (generation !== authGeneration.current) return;
      await switchAuthenticatedQueryScope(queryClient, workspaceUser.id, workspaceId);
      if (generation !== authGeneration.current) return;
      setCurrentWorkspace(workspace);
      localStorage.setItem('currentWorkspaceId', workspaceId);
      setUser(workspaceUser);
      setRoles(workspaceRoles);
      setUsers([]);
      void loadUsers(workspaceId, generation);
      setLocation("/dashboard");
    }
  };

  const refreshSession = useCallback(async () => {
    const generation = authGeneration.current;
    try {
      const { user: sessionUser, roles: sessionRoles } = await api.auth.getUser();
      const workspaces = await api.workspaces.list();
      if (generation !== authGeneration.current) return;
      const userWorkspaces = workspaces.filter((ws: Workspace) => 
        sessionUser.workspaceIds.includes(ws.id)
      );
      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      const activeWorkspace = userWorkspaces.find((ws: Workspace) => ws.id === currentWorkspace?.id)
        || userWorkspaces.find((ws: Workspace) => ws.id === savedWorkspaceId) || userWorkspaces[0];
      const accessChanged = accessSignature(sessionUser, sessionRoles || []) !== accessSignature(user, roles);
      const changed = await switchAuthenticatedQueryScope(queryClient, sessionUser.id, activeWorkspace?.id ?? null, {force:accessChanged});
      if (generation !== authGeneration.current) return;
      if (accessChanged) setAccessVersion(version => version + 1);
      setUser(sessionUser);
      setRoles(sessionRoles || []);
      setAvailableWorkspaces(userWorkspaces);
      setCurrentWorkspace(activeWorkspace || null);
      if (activeWorkspace) {
        localStorage.setItem('currentWorkspaceId', activeWorkspace.id);
        if (changed) {
          setUsers([]);
          void loadUsers(activeWorkspace.id, generation);
        }
      } else {
        setUsers([]);
        localStorage.removeItem('currentWorkspaceId');
      }
      if (!changed) await refreshAccessQueries(queryClient);
    } catch (error) {
      console.error("Failed to refresh session:", error);
    }
  }, [user, roles, currentWorkspace?.id, queryClient]);

  useEffect(() => {
    if (!user?.id) return;
    // A role or personal grant may change while this tab is idle.
    const onFocus = () => { void refreshSession(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user?.id, refreshSession]);

  const switchRole = (userId: string) => {
    console.log("switchRole not implemented with backend auth");
  };

  const addUser = async (newUserData: Omit<User, 'id'> & { password: string }) => {
    if (!currentWorkspace) return;
    const generation = authGeneration.current;
    try {
      const createdUser = await api.users.create({
        ...newUserData,
        workspaceId: currentWorkspace.id,
      });
      if (generation !== authGeneration.current) return createdUser;
      setUsers(prev => [...prev, createdUser]);
      return createdUser;
    } catch (error: any) {
      throw Object.assign(new Error(error.message || t("authPages.failedCreateUser")), {
        missingPermissionIds: error.missingPermissionIds, code: error.code, status: error.status,
      });
    }
  };

  const updateUser = async (updatedUser: User) => {
    if (!currentWorkspace) return;
    const generation = authGeneration.current;
    try {
      const result = await api.users.update(updatedUser.id, {
        ...updatedUser,
        workspaceId: currentWorkspace.id,
      });
      if (generation !== authGeneration.current) return result;
      setUsers(prev => prev.map(u => u.id === result.id ? result : u));
      if (user && user.id === result.id) {
        setUser(result);
        await refreshSession();
      } else {
        await refreshAccessQueries(queryClient);
      }
      return result;
    } catch (error: any) {
      throw Object.assign(new Error(error.message || t("authPages.failedUpdateUser")), {
        missingPermissionIds: error.missingPermissionIds, code: error.code, status: error.status,
      });
    }
  };

  const deleteUser = async (userId: string) => {
    if (!currentWorkspace) return;
    const generation = authGeneration.current;
    try {
      await api.users.delete(userId, currentWorkspace.id);
      if (generation !== authGeneration.current) return;
      setUsers(prev => prev.filter(u => u.id !== userId));
      if (user && user.id === userId) {
        logout();
      }
    } catch (error: any) {
      throw new Error(error.message || t("authPages.failedDeleteUser"));
    }
  };

  const addRole = async (roleData: { name: string; description: string; workspaceId: string | null; isSystem: boolean; permissions: string[]; reason?: string; sourceRoleId?: string }) => {
    if (!currentWorkspace) return;
    const generation = authGeneration.current;
    try {
      const createdRole = await api.roles.create({
        ...roleData,
        workspaceId: currentWorkspace.id,
      });
      if (generation !== authGeneration.current) return createdRole;
      setRoles(prev => [...prev, createdRole]);
      await refreshSession();
      return createdRole;
    } catch (error: any) {
      throw new Error(error.message || t("authPages.failedCreateRole"));
    }
  };

  const updateRole = async (roleId: string, roleData: RoleUpdateDraft) => {
    if (!currentWorkspace) return;
    const generation = authGeneration.current;
    try {
      const updatedRole = await api.roles.update(roleId, {
        ...roleData,
        workspaceId: currentWorkspace?.id,
      });
      if (generation !== authGeneration.current) return updatedRole;
      setRoles(prev => prev.map(r => r.id === updatedRole.id ? updatedRole : r));
      await refreshSession();
      return updatedRole;
    } catch (error: any) {
      throw new Error(error.message || t("authPages.failedUpdateRole"));
    }
  };

  const deleteRole = async (roleId: string, reason?: string) => {
    if (!currentWorkspace) return;
    const generation = authGeneration.current;
    try {
      await api.roles.delete(roleId, currentWorkspace.id, reason);
      if (generation !== authGeneration.current) return;
      setRoles(prev => prev.filter(r => r.id !== roleId));
      await refreshSession();
    } catch (error: any) {
      throw new Error(error.message || t("authPages.failedDeleteRole"));
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      users,
      currentWorkspace,
      availableWorkspaces,
      switchWorkspace,
      login,
      demoLogin,
      register,
      logout, 
      switchRole,
      addUser,
      updateUser,
      deleteUser,
      roles, 
      addRole, 
      updateRole, 
      deleteRole,
      isLoading,
      refreshSession,
    }}>
      {createElement(Fragment, {key:JSON.stringify([user?.id ?? null, currentWorkspace?.id ?? null, accessVersion])}, children)}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Mirrors the explicit system-role keys in server/permissions.ts. These keys
// require an actual grant even for users on a system role; without this
// client-side mirror, navigation can expose screens the API must deny. Keep
// this list in sync with the server whenever a scoped capability is added.
const EXPLICIT_SYSTEM_ROLE_KEYS = new Set<string>([
  "attendance.view","attendance.record","attendance.edit_past_dates","attendance.manage_settings","attendance.decide_late_sick",
  "people_access.all_locations","staff.pay.view","staff.pay.run",
  "branch_cash.view_boxes","branch_cash.view_book","branch_cash.view_held",
  "maintenance.record","maintenance.approve",
  "people_access.manage_settings",
  "workspaces.create",
  "procurement.view_all_locations",
  "purchase_requests.view_all",
  "branch_cash.view",
  "branch_cash.record",
  "branch_cash.manage",
  "expense_setup.manage",
  // Must mirror the canonical server price-write authority. Without this,
  // system warehouse/driver roles can be shown editable price controls that
  // the API correctly rejects.
  "manage_prices",
  "signoffs.approve_any",
  "signoffs.cancel_any",
  "inventory.items_page.view",
  "inventory.recipes.manage",
  "inventory.branch_ingredients.choose",
  "uniforms.view",
  "uniforms.manage_templates",
  "uniforms.manage_garments",
  "uniforms.issue",
  "supplier_receipts.edit_confirmed",
  "supplier_receipts.review_change_requests",
  "suppliers.payments.record",
  "uniforms.review_policy_changes",
  "uniforms.request_over_cap",
  "uniforms.approve_over_cap",
  "analytics.returns_waste.view",
  "analytics.returns_waste.export",
  "returns.view_values",
  "waste.view_costs",
  "customer_orders.manage",
  "sales.upload",
  "reports.consumption.view",
]);

export function hasPermission(user: User | null, permission: Permission, roles: RoleDefinition[] = []): boolean {
  if (!user || !user.roleId) return false;
  const userRole = roles.find(r => r.id === user.roleId);
  if (!userRole) return false;
  if (permission === "procurement.view_all_locations" || permission === "purchase_requests.view_all") {
    return userRole.permissions.some(p => p.id === "procurement.view_all_locations");
  }
  if (permission.startsWith("maintenance.") || permission.startsWith("purchase_requests.")) return userRole.permissions.some(p=>p.id===permission);
  if (userRole.isSystem && isProtectedAdministratorRole(userRole) && !EXPLICIT_SYSTEM_ROLE_KEYS.has(permission)) return true;

  if (userRole.permissions.some(p => p.id === permission)) return true;

  if (permission.includes('.')) {
    const oldKeys = NEW_TO_OLD_PERMISSION_MAP[permission];
    if (oldKeys) {
      for (const oldKey of oldKeys) {
        if (userRole.permissions.some(p => p.id === oldKey)) return true;
      }
    }
  } else {
    const newKeys = OLD_TO_NEW_PERMISSION_MAP[permission];
    if (newKeys) {
      for (const newKey of newKeys) {
        if (userRole.permissions.some(p => p.id === newKey)) return true;
      }
    }
  }

  return false;
}

export function usePermission(permission: Permission): boolean {
  const { user, roles } = useAuth();
  return hasPermission(user, permission, roles);
}
